import { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import Button, { BUTTON_POSITION, BUTTON_TYPE } from '../../components/Button';
import { UserContext } from '../../store/UserProvider';
import { RepositoryContext } from '../../store/RepositoryProvider';
import API from '../../server/api';
import Point from '../../models/point/Point';
import Field from '../../components/Field';
import { ROUTES } from '../../defs/routes';
import Select from '../../components/Select';
import SelectAutocomplete from '../../components/SelectAutocomplete';
import Subdivision from '../../models/subdivision/Subdivision';
import { AutocompleteItemTemplate } from '../../models/AutocompleteItem';

const Accounts = ({ onSave, onCancel }) => {
  const {t} = useTranslation(['common', 'account']);

  const navigate = useNavigate();

  const repository = useContext(RepositoryContext);
  const userInfo = useContext(UserContext);

  const [isLoading, setIsLoading] = useState(false);

  const [roles, setRoles] = useState([]);

  const [sessionAccount, setSessionAccount] = userInfo.useSession('account');
  const [sessionRole, setSessionRole] = userInfo.useSession('role');
  const [, setSessionSubdivision] = userInfo.useSession('subdivision');
  const [sessionPoint, setSessionPoint] = userInfo.useSession('point');

  const [user, setUser] = useState(null);
  const [points, setPoints] = useState([]);
  const [account, setAccount] = useState('');
  const [role, setRole] = useState('');
  const [point, setPoint] = useState(null);
  const [pointId, setPointId] = useState(sessionPoint);
  const [, setUserPoint] = userInfo.useState('point');

  const getUser = () => {
    return userInfo.getUser().then((user) => {
      setUser(user);
      return user;
    }).catch((e) => {
      navigate(ROUTES.AUTH, { replace: true });
      return Promise.reject(e);
    });
  };

  const getPoints = () => {
    repository.list(API.subdivision.subdivisions, { accountId: account }, Subdivision, false).then((subdivisions) => {
      repository.list(API.cashier.schedules, { accountId: account }).then((schedules) => {
        let currentPoint = null;

        schedules.forEach((s) => {
          if (s.isNow() && !currentPoint) {
            currentPoint = s.point.id;
          }
        });

      return repository.list(API.point.autocompleteList, { accountId: account }, AutocompleteItemTemplate(Point), false).then((points) => {
        let haveSessionPoint = false;

        const userAccount = (user || userInfo.user)?.accounts?.filter((a) => a.account.id === account);
        const userSubdivisions = userAccount.length > 0 ? userAccount[0].subdivisions : [];
        if (userSubdivisions && userSubdivisions.length > 0) {
          points = points.filter((p) => userSubdivisions.indexOf(p.meta.subdivision.id) >= 0);
        }

        points.forEach((point) => {
          point.text = `${point.label} (${subdivisions.find((s) => s.id === point.meta.subdivision.id) ? subdivisions.find((s) => s.id === point.meta.subdivision.id).title : ''})`;
          if (point.value === pointId) {
            haveSessionPoint = true;
          }
        });

        setPoints(points);

        let _point = points.find((point) => (!haveSessionPoint && currentPoint !== null) ? ( point.value === currentPoint) : (point.value === pointId));
        setPoint(_point || points[0]);
        setIsLoading(false);
        return points;
      }).catch((e) => {
        setPoints([]);
        setIsLoading(false);
        return e;
      });
      });
    }).catch((e) => {
      setPoints([]);
      setPoint(null);
      setIsLoading(false);
      return e;
    });
  };
;

  useEffect(() => {
    getUser();
    setAccount(sessionAccount);
    setRole(sessionRole);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (account && user) {
      if (userInfo.getSession().isCashier()) {
        getPoints();
      }

      let acc = user.getAccount(account);

      if (acc) {
        setRoles(acc.roles);
        setIsLoading(false);
      } else {
        setRoles([]);
      }
    } else {
      setRoles([]);
    }
  }, [account]);

  const handleSave = () => {
    let needReload = !point || sessionRole !== role || sessionPoint !== point.value || sessionAccount !== account;

    setSessionAccount(account);
    setSessionRole(role);
    setSessionPoint(userInfo.getSession().isCashier() && point ? point.value : null);
    setSessionSubdivision(userInfo.getSession().isCashier() && point ? point.meta.subdivision.id : null);
    setUserPoint(point ? point.meta : null);

    onSave && onSave(needReload);
  };

  const handleChangeAccount = (account) => {
    setIsLoading(true);
    setAccount(account);
    setPointId(null);
  };

  const handleCancel = useCallback(() => {
    onCancel && onCancel();
  }, [onCancel]);

  return (
    <div className="trip-app">
      <header className={`trip-app-header`}>
        <h1>{t('account:accounts.title')}</h1>
      </header>
      <div className="trip-app-body">
        <Field title={t('account:accounts.company')}>
          <Select
            value={account}
            options={user && user.accounts ? user.accounts.map((acc) => (
              { value: acc.account.id, text: acc.accountTitle }
            )) : []}
            onSelect={handleChangeAccount}
          />
        </Field>
        <Field title={t('account:accounts.role')}>
          <Select
            value={role}
            options={roles.map((role) => (
              { value: role, text: t(`role.${role}`) }
            ))}
            onSelect={setRole}
          />
        </Field>
        {userInfo.getSession().isCashier() && (
          <Field title={t('account:accounts.point')}>
            <SelectAutocomplete
              list={points}
              template={(p) => p ? p.text : ''}
              value={point}
              onChange={setPoint}
              // getValue={(row) => row ? row.label : ''}
            />
          </Field>
        )}
      </div>
      <footer className="trip-app-footer">
        <Button.Panel>
          <Button position={BUTTON_POSITION.LEFT} variant={BUTTON_TYPE.PRIMARY} onClick={handleSave} disabled={isLoading}>{t('common:button.apply')}</Button>
          <Button position={BUTTON_POSITION.RIGHT} variant={BUTTON_TYPE.SECONDARY} onClick={handleCancel}>{t('common:button.cancel')}</Button>
        </Button.Panel>
      </footer>
    </div>
  )
}

Accounts.propTypes = {
  onSave: PropTypes.func,
  onCancel: PropTypes.func,
};

export default Accounts;